import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type DragEvent, type ClipboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X, Minus, Maximize2, Send, Bot, User, Wrench, Info, Loader2, ChevronDown, Check, ImagePlus, ArrowDown } from 'lucide-react';
import { useAIStore, type AIChatMessage } from '../../stores/aiStore.js';
import { useAIController } from '../../hooks/useAIController.js';
import { useCanvasContextStore } from '../../stores/canvasContextStore.js';
import { useFilesContextStore } from '../../stores/filesContextStore.js';
import { aiService, type AIProfile } from '../../services/ai.service.js';
import { cn } from '../../lib/utils.js';
import { MarkdownContent } from './MarkdownContent.js';
import { useAttachments } from '../../hooks/useAttachments.js';

export function AIChatWidget() {
  const navigate = useNavigate();
  const {
    chatOpen, chatMinimized, chatPosition, chatSize, connected, messages, isTyping,
    toggleChat, closeChat, toggleMinimize, setChatPosition, activeProfileId, profiles, setProfiles,
    pendingInput, setPendingInput,
  } = useAIStore();
  const { sendMessage, executeTool } = useAIController();

  const [input, setInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { attachments: chatAttachments, error: attachmentError, addFiles, removeFile, clearFiles, getPayload, hasAttachments } = useAttachments();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track if user is scrolled up (not at bottom)
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(messages.length);

  // Consume pendingInput when chat opens
  useEffect(() => {
    if (chatOpen && pendingInput) {
      setInput((prev) => (prev ? prev + '\n' + pendingInput : pendingInput));
      setPendingInput(null);
    }
  }, [chatOpen, pendingInput, setPendingInput]);

  // Fetch profiles when widget opens
  useEffect(() => {
    if (chatOpen && profiles.length === 0) {
      loadProfiles();
    }
  }, [chatOpen, profiles.length]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const loadProfiles = async () => {
    setProfilesLoading(true);
    try {
      const result = await aiService.listProfiles({
        isActive: 'true',
        visibility: 'global',
        limit: 50,
      });
      setProfiles(result.data);
    } catch (err) {
      console.error('Failed to load profiles:', err);
    } finally {
      setProfilesLoading(false);
    }
  };

  // Check if user is at the bottom of the scroll container
  const isAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    // Consider "at bottom" if within 50px of the bottom
    return scrollHeight - scrollTop - clientHeight < 50;
  }, []);

  // Handle scroll event to track user scroll position
  const handleScroll = useCallback(() => {
    const atBottom = isAtBottom();
    setIsScrolledUp(!atBottom);
    if (atBottom) {
      setHasNewMessages(false);
    }
  }, [isAtBottom]);

  // Auto-scroll to bottom on new messages (only if user is at bottom)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Detect new messages
    if (messages.length > prevMessageCountRef.current) {
      if (isAtBottom()) {
        // User is at bottom, auto-scroll
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        setHasNewMessages(false);
      } else {
        // User scrolled up, show new messages indicator
        setHasNewMessages(true);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, isTyping, isAtBottom]);

  // Scroll to bottom when typing stops (useful for streaming completion)
  useEffect(() => {
    if (!isTyping && hasNewMessages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setHasNewMessages(false);
      setIsScrolledUp(false);
    }
  }, [isTyping, hasNewMessages]);

  // Scroll to bottom button handler
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setHasNewMessages(false);
    setIsScrolledUp(false);
  };

  // Calculate default position (bottom-right)
  const pos = chatPosition.x === -1
    ? { x: window.innerWidth - chatSize.width - 24, y: window.innerHeight - chatSize.height - 24 }
    : chatPosition;

  const isBusy = isTyping;

  // Get active profile info
  const activeProfile = profiles.find(p => p.id === activeProfileId) || profiles[0];
  const displayProfile = activeProfile || profiles[0];

  const handleSend = () => {
    const msg = input.trim();
    if ((!msg && !hasAttachments) || isBusy) return;
    setInput('');

    // Check for tool commands: /tool <name> [json params]
    if (msg.startsWith('/tool ')) {
      const parts = msg.slice(6).split(' ');
      const toolName = parts[0];
      let params: Record<string, unknown> = {};
      if (parts.length > 1) {
        try { params = JSON.parse(parts.slice(1).join(' ')); } catch { /* ignore parse errors */ }
      }
      executeTool(toolName, params);
      useAIStore.getState().addMessage({
        id: `user-${Date.now()}`,
        role: 'user',
        content: msg,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    // Check for navigation commands
    if (msg.startsWith('/go ')) {
      const path = msg.slice(4).trim();
      navigate(path.startsWith('/') ? path : `/${path}`);
      useAIStore.getState().addMessage({
        id: `user-${Date.now()}`,
        role: 'user',
        content: msg,
        timestamp: new Date().toISOString(),
      });
      useAIStore.getState().addMessage({
        id: `sys-${Date.now()}`,
        role: 'system',
        content: `Navigated to ${path}`,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    const payload = hasAttachments ? getPayload().map(a => ({ ...a, id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` })) : undefined;
    const canvasCtx = useCanvasContextStore.getState().context;
    const filesCtx = useFilesContextStore.getState().context;
    let awareness: Record<string, unknown> | undefined;
    if (filesCtx) {
      awareness = { files: filesCtx, module: 'files' };
    } else if (canvasCtx) {
      awareness = { canvas: canvasCtx, module: 'canvas' };
    }
    sendMessage(msg || 'Bu görseli analiz et.', undefined, activeProfileId ?? undefined, payload, awareness);
    clearFiles();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const handleDragOver = (e: DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length) addFiles(files);
  };

  // Drag handlers
  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setChatPosition({
        x: Math.max(0, Math.min(window.innerWidth - 100, dragRef.current.origX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.origY + dy)),
      });
    };

    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [pos.x, pos.y, setChatPosition]);

  // FAB button when chat is closed
  if (!chatOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition-all hover:bg-primary-700 hover:scale-105 active:scale-95"
        aria-label="Open AI Chat"
      >
        <MessageSquare className="h-6 w-6" />
        {connected && (
          <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-400 ring-2 ring-white dark:ring-gray-900" />
        )}
      </button>
    );
  }

  return (
    <div
      ref={widgetRef}
      className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
      style={{
        left: pos.x,
        top: pos.y,
        width: chatSize.width,
        height: chatMinimized ? 48 : chatSize.height,
        transition: dragRef.current ? 'none' : 'height 0.2s ease',
      }}
    >
      {/* Header — draggable */}
      <div
        onMouseDown={onDragStart}
        className="flex h-12 shrink-0 cursor-grab items-center justify-between border-b border-gray-200 bg-primary-600 px-3 text-white dark:border-gray-700 active:cursor-grabbing"
      >
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <span className="text-sm font-semibold">Elmes AI</span>
          <span className={cn('h-2 w-2 rounded-full', connected ? 'bg-green-400' : 'bg-red-400')} />
        </div>

        {/* Profile Selector Dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen(!dropdownOpen);
            }}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-sm hover:bg-white/20 transition-colors"
            title="Select AI Model"
          >
            {displayProfile ? (
              <>
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full text-xs"
                  style={{ backgroundColor: displayProfile.color || '#6366f1' }}
                >
                  {displayProfile.avatarEmoji || '🤖'}
                </span>
                <span className="max-w-[100px] truncate text-xs">{displayProfile.name}</span>
              </>
            ) : (
              <span className="text-xs">Model Seç</span>
            )}
            <ChevronDown className={cn('h-3 w-3 transition-transform', dropdownOpen && 'rotate-180')} />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div
              className="absolute left-0 top-full mt-1 w-64 max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800 z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">AI Model Seçin</span>
              </div>
              {profilesLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              ) : profiles.length === 0 ? (
                <div className="p-4 text-sm text-gray-500 text-center">Profil bulunamadı</div>
              ) : (
                <div className="p-1">
                  {profiles.map((profile) => (
                    <ProfileOption
                      key={profile.id}
                      profile={profile}
                      isActive={profile.id === activeProfileId || (!activeProfileId && profile === profiles[0])}
                      onSelect={() => {
                        useAIStore.getState().setActiveProfile(profile.id);
                        setDropdownOpen(false);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => navigate('/ai')} className="rounded p-1 hover:bg-white/20" title="Full page">
            <Maximize2 className="h-4 w-4" />
          </button>
          <button onClick={toggleMinimize} className="rounded p-1 hover:bg-white/20" title="Minimize">
            <Minus className="h-4 w-4" />
          </button>
          <button onClick={closeChat} className="rounded p-1 hover:bg-white/20" title="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages area */}
      {!chatMinimized && (
        <>
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin relative"
          >
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm gap-2">
                <Bot className="h-10 w-10 opacity-40" />
                <p>Elmes AI'a mesaj gönderin</p>
                {displayProfile && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span
                      className="flex h-4 w-4 items-center justify-center rounded-full text-[8px]"
                      style={{ backgroundColor: displayProfile.color || '#6366f1' }}
                    >
                      {displayProfile.avatarEmoji || '🤖'}
                    </span>
                    <span>{displayProfile.name}</span> ile sohbet
                  </div>
                )}
                <div className="text-xs text-gray-300 dark:text-gray-600 space-y-1 text-center">
                  <p>/tool system.status — Tool çalıştır</p>
                  <p>/go dashboard — Sayfaya git</p>
                </div>
              </div>
            )}
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}
            {isTyping && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>AI düşünüyor...</span>
              </div>
            )}
            <div ref={messagesEndRef} />

            {/* New messages indicator button */}
            {hasNewMessages && (
              <button
                onClick={scrollToBottom}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-sm text-white shadow-lg hover:bg-primary-700 transition-colors"
              >
                <ArrowDown className="h-4 w-4" />
                <span>Yeni mesajlar</span>
              </button>
            )}
          </div>

          {/* Input area */}
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-2">
            {/* Attachment previews */}
            {chatAttachments.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1.5">
                {chatAttachments.map((att) => (
                  <div key={att.id} className="group relative">
                    <img src={att.previewUrl} alt={att.fileName} className="h-10 w-10 rounded border border-gray-200 object-cover dark:border-gray-600" />
                    <button onClick={() => removeFile(att.id)} className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {attachmentError && <p className="mb-1 text-[10px] text-red-500">{attachmentError}</p>}
            <div className={cn('flex items-end gap-2', isDragging && 'rounded-lg ring-2 ring-primary-500')}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            >
              <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/gif,image/webp" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={!connected || isBusy}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-600 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-40"
                title="Görsel ekle"
              >
                <ImagePlus className="h-4 w-4" />
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={!connected ? 'Bağlantı bekleniyor...' : isBusy ? 'Cevap bekleniyor...' : 'Mesaj yazın...'}
                disabled={!connected || isBusy}
                rows={1}
                className="flex-1 resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!connected || (!input.trim() && !hasAttachments) || isBusy}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white transition-colors hover:bg-primary-700 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ProfileOption({
  profile,
  isActive,
  onSelect,
}: {
  profile: AIProfile;
  isActive: boolean;
  onSelect: () => void;
}) {
  const typeLabels: Record<string, string> = {
    assistant: 'Asistan',
    automation: 'Otomasyon',
    api: 'API',
    developer: 'Geliştirici',
  };

  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
        isActive
          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-200'
      )}
    >
      {/* Avatar */}
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm"
        style={{ backgroundColor: profile.color || '#6366f1' }}
      >
        {profile.avatarEmoji || '🤖'}
      </span>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate">{profile.name}</span>
          {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-primary-600" />}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: profile.color || '#6366f1' }}
          />
          <span>{typeLabels[profile.type] || profile.type}</span>
        </div>
      </div>
    </button>
  );
}

function ChatBubble({ message }: { message: AIChatMessage }) {
  const isUser = message.role === 'user';
  const isTool = message.role === 'tool';
  const isSystem = message.role === 'system';

  return (
    <div className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white',
          isTool ? 'bg-amber-500' : isSystem ? 'bg-gray-500' : 'bg-primary-600',
        )}>
          {isTool ? <Wrench className="h-3.5 w-3.5" /> : isSystem ? <Info className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
        </div>
      )}
      <div className={cn(
        'max-w-[85%] rounded-xl px-3 py-2 text-sm',
        isUser
          ? 'bg-primary-600 text-white'
          : isTool
            ? 'bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200 border border-amber-200 dark:border-amber-800'
            : isSystem
              ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 text-xs italic'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      )}>
        {isUser ? (
          <>
            {message.attachments && message.attachments.length > 0 && (
              <div className="mb-1 flex flex-wrap gap-1">
                {message.attachments.map((att) => (
                  <img
                    key={att.id}
                    src={att.base64Data ? `data:${att.mimeType};base64,${att.base64Data}` : att.thumbnailUrl || ''}
                    alt={att.fileName}
                    className="h-16 w-16 cursor-pointer rounded object-cover opacity-90 transition-opacity hover:opacity-100"
                    onClick={() => {
                      const src = att.base64Data ? `data:${att.mimeType};base64,${att.base64Data}` : att.thumbnailUrl || '';
                      window.open(src, '_blank');
                    }}
                  />
                ))}
              </div>
            )}
            <p className="whitespace-pre-wrap break-words">{message.content}</p>
          </>
        ) : (
          <div className="break-words [&>*:first-child]:mt-0">
            <MarkdownContent content={message.content} />
          </div>
        )}
        {isTool && message.toolResult != null && (
          <pre className="mt-1.5 max-h-32 overflow-auto rounded bg-black/5 p-1.5 text-xs dark:bg-white/5">
            {String(typeof message.toolResult === 'string' ? message.toolResult : JSON.stringify(message.toolResult, null, 2))}
          </pre>
        )}
        <span className="mt-0.5 block text-[10px] opacity-50">
          {new Date(message.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      {isUser && (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          <User className="h-3.5 w-3.5" />
        </div>
      )}
    </div>
  );
}
